import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import App from './pages/App';
import reportWebVitals from './reportWebVitals';
import * as Sentry from '@sentry/react';
import Results from './pages/Results';
import Schedule from './pages/Schedule';
import Favorites from './pages/Favorites';
import Profile from './pages/Profile';
import SharedFavoritesInvite from './pages/SharedFavoritesInvite';
import SharedScheduleInvite from './pages/SharedScheduleInvite';
import Checkout from './pages/Checkout';
import { CampProvider } from './context/CampContext';
import { UserProvider } from './context/UserContext';
import AppAbout from './pages/About';
import AppTeam from './pages/Team';
import AppCareers from './pages/Careers';
import AppPartners from './pages/Partners';
import Terms from './pages/Terms';
import BookingTerms from './pages/BookingTerms';
import Organizer from './pages/Organizer';
import OrganizerCampListing from './pages/OrganizerCampListing';
import OrganizerAdmin from './pages/OrganizerAdmin';
import OrganizerList from './pages/OrganizerList';
import OrganizerAdminCampBookings from './pages/OrganizerAdminCampBookings';
import DiscountAdmin from './pages/DiscountAdmin';
import CampPage from './pages/CampPage';
import Confirmation from './pages/Confirmation';
import { NotificationProvider } from './context/NotificationContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: "https://5dcff89902e914ee8c418ace7285c4a6@o4510183403552768.ingest.us.sentry.io/4510183404601344",
    sendDefaultPii: true
  });
}

const appRouter = createBrowserRouter([
  { path: '/', element: <App />},
  { path: '/app', element: <App />},
  { path: '/search', element: <Results />},
  { path: '/schedule', element: <Schedule />},
  { path: '/favorites', element: <Favorites />},
  { path: '/profile', element: <Profile />},
  { path: '/checkout', element: <Checkout />},
  { path: '/confirmation', element: <Confirmation />},
  { path: '/terms', element: <Terms /> },
  { path: '/booking-terms', element: <BookingTerms /> },
  { path: '/organizer/:organizerId', element: <Organizer /> },
  { path: '/organizer/:organizerId/embedded', element: <OrganizerCampListing /> },
  { path: '/admin/discount', element: <DiscountAdmin /> },
  { path: '/admin/organizer', element: <OrganizerList /> },
  { path: '/admin/organizer/:organizerId', element: <OrganizerAdmin /> },
  { path: '/admin/organizer/:organizerId/camps/:campId', element: <OrganizerAdminCampBookings /> },
  { path: '/admin/organizer/:organizerId/camps/:campId/dates/:dateId', element: <OrganizerAdminCampBookings /> },
  { path: '/camp/:id', element: <CampPage /> },
  { path: '/about', element: <AppAbout /> },
  { path: '/team', element: <AppTeam /> },
  { path: '/careers', element: <AppCareers /> },
  { path: '/partners', element: <AppPartners /> },
  { path: '/app/about', element: <AppAbout /> },
  { path: '/app/team', element: <AppTeam /> },
  { path: '/app/careers', element: <AppCareers /> },
  { path: '/app/partners', element: <AppPartners /> },
  { path: '/favorites/invite/:favoriteUuid', element: <SharedFavoritesInvite />},
  { path: '/schedule/invite/:scheduleUuid', element: <SharedScheduleInvite />},
]);

const queryClient = new QueryClient();

// Component to determine which router to use based on current path
const AppRouter: React.FC = () => {
  return (
    <AnalyticsProvider>
      <NotificationProvider>
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <FeatureFlagProvider>
              <CampProvider>
                <RouterProvider router={appRouter} />
              </CampProvider>
            </FeatureFlagProvider>
          </UserProvider>
        </QueryClientProvider>
      </NotificationProvider>
    </AnalyticsProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
      <AppRouter />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
